import { effect, inject, Injectable, linkedSignal } from '@angular/core';
import { AppsettingsService } from '../appsettings/appsettings.service';
import { BoardConfig } from '../../../interfaces/board-config.interface';
import { AnalyticsMenuPointService } from '../analytics-menupoint/analytics-menupoint.service';
import { UserGeneratedWidgetConfigService } from '../user-generated-widget-config/user-generated-widget-config.service';
import { IndicatorWidgetConfigService } from '../indicator-widget-config/indicator-widget-config.service';
import { uuidv7 } from 'uuidv7';
import { BoardType } from '../../../enums/board-type.enum';
import { normalizeBoards } from '../chart-migration/normalize-boards.util';

@Injectable({ providedIn: 'root' })
export class BoardService {
	private appSettingsService = inject(AppsettingsService);
	private menuPointService = inject(AnalyticsMenuPointService);
	private userGeneratedWidgetConfigService = inject(UserGeneratedWidgetConfigService);
	private indicatorWidgetConfigService = inject(IndicatorWidgetConfigService);

	private readonly _boards = linkedSignal<BoardConfig[]>(() =>
		normalizeBoards(this.appSettingsService.webAppSettings?.boards ?? [])
	);

	readonly boards = this._boards.asReadonly();

	constructor() {
		effect(() => {
			const original = this.appSettingsService.webAppSettings?.boards;
			if (!original?.length) return;
			const normalized = normalizeBoards(original);
			const changed = normalized.some((board, i) => board !== original[i]);
			if (!changed) return;
			this.appSettingsService.webAppSettings.boards = normalized;
			this.appSettingsService.saveAppSettingsOnServer();
		});
	}

	getBoard(id: string | null | undefined): BoardConfig | undefined {
		if (!id) return undefined;
		return this._boards().find((board) => board.id === id);
	}

	createBoard(board: Omit<BoardConfig, 'id'>): BoardConfig {
		const boardId = uuidv7();

		const userGeneratedWidgets =
			board.userGeneratedWidgets?.map((widget) => ({
				...widget,
				id: uuidv7(),
			})) ?? [];

		const indicatorWidgets =
			board.indicatorWidgets?.map((widget) => ({
				...widget,
				id: uuidv7(),
			})) ?? [];

		const newBoard: BoardConfig = {
			...board,
			id: boardId,
			userGeneratedWidgets,
			indicatorWidgets,
		};

		const boards = [...this._boards(), newBoard];
		this._boards.set(boards);
		this.appSettingsService.webAppSettings.boards = boards;

		this.appSettingsService.saveAppSettingsOnServer();
		this.menuPointService.reloadMenuPoints();

		return newBoard;
	}

	updateBoard(id: string, updates: Partial<Omit<BoardConfig, 'id'>>): void {
		const boards = [...this._boards()];
		const index = boards.findIndex((b) => b.id === id);

		if (index === -1) {
			return;
		}

		boards[index] = { ...boards[index], ...updates } as BoardConfig;

		this._boards.set(boards);
		this.appSettingsService.webAppSettings.boards = boards;

		this.appSettingsService.saveAppSettingsOnServer();
		this.menuPointService.reloadMenuPoints();
	}

	addWidgetsToBoard(
		boardId: string,
		widgets: BoardConfig['userGeneratedWidgets'] | BoardConfig['indicatorWidgets']
	): void {
		const board = this.getBoard(boardId);
		if (!board) return;

		const configService =
			board.type === BoardType.DASHBOARD
				? this.userGeneratedWidgetConfigService
				: this.indicatorWidgetConfigService;

		widgets.forEach((widget) => {
			configService.saveConfig(widget, false, board.id);
		});
	}

	moveWidget(sourceBoardId: string, targetBoardId: string, widgetId: string): void {
		const sourceBoard = this.getBoard(sourceBoardId);
		const targetBoard = this.getBoard(targetBoardId);

		if (!sourceBoard || !targetBoard) return;

		let widgetToMove: any;
		if (sourceBoard.type === BoardType.DASHBOARD) {
			widgetToMove = sourceBoard.userGeneratedWidgets?.find((w) => w.id === widgetId);
			if (widgetToMove) {
				sourceBoard.userGeneratedWidgets = sourceBoard.userGeneratedWidgets?.filter(
					(w) => w.id !== widgetId
				);
			}
		} else {
			widgetToMove = sourceBoard.indicatorWidgets?.find((w) => w.id === widgetId);
			if (widgetToMove) {
				sourceBoard.indicatorWidgets = sourceBoard.indicatorWidgets?.filter(
					(w) => w.id !== widgetId
				);
			}
		}

		if (!widgetToMove) return;

		const movedWidget = { ...structuredClone(widgetToMove), id: undefined };

		const targetConfigService =
			targetBoard.type === BoardType.DASHBOARD
				? this.userGeneratedWidgetConfigService
				: this.indicatorWidgetConfigService;

		targetConfigService.saveConfig(movedWidget, false, targetBoard.id);

		this._boards.set([...this._boards()]);
		this.appSettingsService.webAppSettings.boards = this._boards();
		this.appSettingsService.saveAppSettingsOnServer();
	}

	reorderBoards(newOrderIds: string[]): void {
		const current = this._boards();
		const idToBoard = new Map(current.map((b) => [b.id, b]));

		const reordered = newOrderIds
			.map((id) => idToBoard.get(id))
			.filter((b): b is BoardConfig => b !== undefined);

		this._boards.set(reordered);
		this.appSettingsService.webAppSettings.boards = reordered;
		this.appSettingsService.saveAppSettingsOnServer();
		this.menuPointService.reloadMenuPoints();
	}

	deleteBoard(id: string): void {
		const updated = this._boards().filter((b) => b.id !== id);
		this._boards.set(updated);
		this.appSettingsService.webAppSettings.boards = updated;
		this.appSettingsService.saveAppSettingsOnServer();
		this.menuPointService.reloadMenuPoints();
	}
}
